import { useEffectOnce } from '@trmediaab/zebra-hooks';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import BetConfig from 'atg/api/BetConfig';
import BetType from 'atg/api/BetType';
import guaranteeOptions from 'atg/constants/GuaranteeOptions';
import { clone } from 'atg/utils/Utils';
import Button from 'shared/components/button/Button';
import HelpButton from 'shared/components/button/HelpButton';
import CheckboxInput from 'shared/components/input/CheckboxInput';
import RadioInput from 'shared/components/input/RadioInput';
import SelectInput from 'shared/components/input/SelectInput';
import { fetchSettings } from 'shared/redux/actions';
import { settings, user } from 'shared/redux/selectors';
import { formatDateTime } from 'shared/utils/dateUtils';
import moment from 'shared/utils/moment';
import { getText } from 'shared/utils/Text';

import PublishTimePicker from './PublishTimePicker';
import TemplatePicker from './TemplatePicker';

const BetConfigDialog = ({
  closeHandler,
  betKey,
  betKeyInfo,
  betConfig,
  isUsingFactorReduction,
  isNewGame,
  onConfirmNewConfig,
}) => {
  const dispatch = useDispatch();

  const isLocked = betConfig.isLocked();

  const betTypeName = betKey.getBetType();
  const betType = BetType.getBetType(betTypeName);
  const hasV6 = betType.mayHaveV6() && betKeyInfo.allowsAllIn();
  const hasV7 = betType.mayHaveV7() && betKeyInfo.allowsAllIn();

  const userSettings = useSelector(settings.getSettings);
  const hasLoadedSettings = !!userSettings;

  const userSettingsV6 = useSelector(settings.v6);
  const userSettingsV7 = useSelector(settings.v7);
  const userSettingsSpareType = useSelector(settings.spareType);

  const [name, setName] = useState(
    betConfig.getName() || getText('dialog-bet-config-default-name'),
  );
  const [numberOfSystems, setNumberOfSystems] = useState(
    betConfig.getNumberOfSystems() || betType.getNumberOfSystemsOptions()[0],
  );
  const [
    numberOfFaultingReductionsToTolerate,
    setNumberOfFaultingReductionsToTolerate,
  ] = useState(
    betConfig.getNumberOfFaultingReductionsToTolerate() ||
      betType.getNumberOfFaultingReductionsToTolerateOptions()[0],
  );

  const [raceNumber, setRaceNumber] = useState(
    betConfig.getRaceNumber() || betKeyInfo.getRaceNumbers()[0],
  );

  const [v6, setV6] = useState(
    betConfig.hasV6() ? betConfig.getV6() : undefined,
  );

  const [v7, setV7] = useState(
    betConfig.hasV7() ? betConfig.getV7() : undefined,
  );

  const [spareType, setSpareType] = useState(betConfig.getSpareType());

  // Ensure that v6, v7 and spareType (if they apply to this bet type)
  // are given default values once settings are loaded
  useEffect(() => {
    if (hasLoadedSettings) {
      if (hasV6 && v6 === undefined) {
        setV6(userSettingsV6);
      }
      if (hasV7 && v7 === undefined) {
        setV7(userSettingsV7);
      }
      if (ALLOW_SPARES && betType.hasSpareType() && !spareType) {
        setSpareType(userSettingsSpareType);
      }
    }
  }, [
    betType,
    hasLoadedSettings,
    hasV6,
    hasV7,
    spareType,
    userSettingsSpareType,
    userSettingsV6,
    userSettingsV7,
    v6,
    v7,
  ]);

  const [stake, setStake] = useState(
    betConfig.getStake() || betType.getStakeOptions()[0],
  );
  const [guaranteePercent, setGuaranteePercent] = useState(
    betConfig.getGuaranteePercent() || betType.getGuaranteePercentOptions()[0],
  );

  const numberOfErrorsGuaranteeAllowed = !v6 && !v7 && !isUsingFactorReduction;

  const [guaranteeIndex, setGuaranteeIndex] = useState(
    // The initial guarantee index will be "percent" (last index option) if
    // either a percentage value has been chosen, or if numberOfErrorsGuarantee
    // isn't allowed due to v6 / v7 / factors
    (guaranteePercent > 0 && guaranteePercent < 100) ||
      !numberOfErrorsGuaranteeAllowed
      ? guaranteeOptions[betTypeName]?.length
      : betConfig.getNumberOfErrorsGuarantee(),
  );

  // If v6/v7 is selected, the "percent" (last) guarantee option is enforced
  // instead of the manually selected guarantee index. If v6/v7 is deselected,
  // the manually selected option will once again show.
  const computedGuaranteeIndex =
    guaranteeOptions[betTypeName]?.length > 0 && (v6 || v7)
      ? guaranteeOptions[betTypeName].length
      : guaranteeIndex;

  const isPercentGuaranteeIndex =
    computedGuaranteeIndex === guaranteeOptions[betTypeName]?.length;

  const [template, setTemplate] = useState(null);

  const userIsTipCreator = useSelector(user.hasRole('ROLE_GAME_TIP_CREATOR'));

  const userEmployer = useSelector(user.getEmployer);
  const [isPublished, setIsPublished] = useState(betConfig.isPublished());
  const [nowMoment, setNowMoment] = useState(moment());
  const [publishDate, setPublishDate] = useState(
    betConfig.getPublishDate()
      ? betConfig.getPublishDate().moment
      : nowMoment.hours() < 15
        ? moment({ hour: 15 })
        : nowMoment.clone(),
  );
  const onPublishDateChange = (unit, value) => {
    const newPublishDate = publishDate.clone();
    // unit = moment getter/setter function name: month, date, hour, minute
    newPublishDate[unit](value);
    const now = moment();
    setPublishDate(newPublishDate.isBefore(now) ? now : newPublishDate);
    setNowMoment(now);
  };

  const onNameFocus = useCallback(evt => {
    if (evt.target.value === getText('dialog-bet-config-default-name')) {
      setName('');
    }
  }, []);

  const onNameBlur = useCallback(evt => {
    if (evt.target.value.trim() === '') {
      setName(getText('dialog-bet-config-default-name'));
    }
  }, []);

  const onConfirmConfig = () => {
    const newBetConfig = new BetConfig(clone(betConfig.data));

    newBetConfig.setName(
      name.trim() === '' ? getText('dialog-bet-config-default-name') : name,
    );

    if (!isLocked) {
      if (typeof v6 === 'boolean') {
        newBetConfig.setV6(v6);
      }
      if (typeof v7 === 'boolean') {
        newBetConfig.setV7(v7);
      }

      if (betType.hasGuaranteePercent()) {
        if (!betType.hasExtendedGuarantee() || isPercentGuaranteeIndex) {
          newBetConfig.deleteObject('numberOfErrorsGuarantee');
          newBetConfig.setGuaranteePercent(guaranteePercent);
        } else {
          newBetConfig.deleteObject('guaranteePercent');
          newBetConfig.setNumberOfErrorsGuarantee(guaranteeIndex);
        }
      }

      newBetConfig.setNumberOfSystems(numberOfSystems);
      newBetConfig.setNumberOfFaultingReductionsToTolerate(
        numberOfFaultingReductionsToTolerate,
      );
      newBetConfig.setStake(stake);
      newBetConfig.setSpareType(spareType);
      if (betType.hasRaceNumber()) {
        newBetConfig.setRaceNumber(raceNumber);
      }

      if (userIsTipCreator) {
        newBetConfig.setPublished(isPublished || false);
        newBetConfig.setPublishDate(publishDate.valueOf());
      }

      const templateId = Number.parseInt(template, 10);
      if (templateId > -1) {
        newBetConfig.setTemplate(templateId);
      }
    }
    onConfirmNewConfig(newBetConfig, closeHandler);
  };

  useEffectOnce(() => {
    dispatch(fetchSettings());
  });

  const onSubmit = evt => {
    evt.preventDefault();
    onConfirmConfig();
  };

  return (
    <>
      <form onSubmit={onSubmit}>
        <div className="time bet-config-time">
          {formatDateTime(betKeyInfo.getTime().getTime())}
        </div>

        <div className="fgrid fgrid--full small-fgrid--1of3">
          <div className="fgrid-cell--autoSize">
            <h1 className="title dialog-title">
              {getText(`bet-type-long_${betKey.getBetType().toLowerCase()}`)},{' '}
              {betKeyInfo.getTrack().getName()}
            </h1>
          </div>
          <div className="fgrid-cell fgrid-cell--right">
            <input
              type="text"
              className="text-input-with-help"
              value={name}
              onChange={evt => setName(evt.target.value)}
              onFocus={onNameFocus}
              onBlur={onNameBlur}
            />
            <HelpButton
              header={getText('help-system-name-header')}
              body={getText('help-system-name-body')}
            />
          </div>
        </div>

        <hr className="mq-small-show" />

        <div className="fgrid fgrid--dialog-form fgrid-dialog-form-wide-labels">
          {betType.hasStake() && (
            <>
              <div className="fgrid-cell fgrid-cell-label">
                {getText('dialog-bet-config-stake')}
              </div>
              <div className="fgrid-cell fgrid-cell-field">
                <SelectInput
                  width="80"
                  value={stake}
                  disabled={isLocked}
                  onChange={evt => setStake(evt.target.value)}
                >
                  {betType.getStakeOptions().map(option => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </SelectInput>
              </div>
            </>
          )}
          {betType.hasNumberOfSystems() && (
            <>
              <div className="fgrid-cell fgrid-cell-label">
                {getText('dialog-bet-config-number-of-systems')}
                <HelpButton
                  header={getText('help-number-of-systems-header')}
                  body={getText('help-number-of-systems-body')}
                />
              </div>
              <div className="fgrid-cell fgrid-cell-field">
                <SelectInput
                  width="80"
                  value={numberOfSystems}
                  disabled={isLocked}
                  onChange={evt => setNumberOfSystems(evt.target.value)}
                >
                  {betType
                    .getNumberOfSystemsOptions()
                    .map(numberOfSystemsOption => (
                      <option
                        key={numberOfSystemsOption}
                        value={numberOfSystemsOption}
                      >
                        {numberOfSystemsOption}
                      </option>
                    ))}
                </SelectInput>
              </div>
            </>
          )}
          {betType.hasGuaranteePercent() && !betType.hasExtendedGuarantee() && (
            <>
              <div className="fgrid-cell fgrid-cell-label">
                {getText('dialog-bet-config-guarantee-percent')}
                <HelpButton
                  header={getText('help-guarantee-percent-header')}
                  body={getText('help-guarantee-percent-body')}
                />
              </div>
              <div className="fgrid-cell fgrid-cell-field">
                <label>
                  <SelectInput
                    width="80"
                    className="inline-dropdown"
                    value={guaranteePercent}
                    disabled={isLocked}
                    onChange={evt => setGuaranteePercent(evt.target.value)}
                  >
                    {betType.getGuaranteePercentOptions().map(option => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </SelectInput>
                  <span className="dropdown-after-label">%</span>
                </label>
              </div>
            </>
          )}
        </div>

        {betType.hasNumberOfFaultingReductionsToTolerate() && (
          <div className="fgrid fgrid--dialog-form fgrid-dialog-form-wide-labels">
            <div className="fgrid-cell fgrid-cell-label">
              {getText('dialog-bet-config-tolerate-faulting-reductions-label')}
              <HelpButton
                header={getText('help-reduction-tolerate-header')}
                body={getText('help-reduction-tolerate-body')}
              />
            </div>
            <div className="fgrid-cell fgrid-cell-field">
              <label>
                <SelectInput
                  className="inline-dropdown"
                  width="80"
                  value={numberOfFaultingReductionsToTolerate}
                  disabled={isLocked}
                  onChange={evt =>
                    setNumberOfFaultingReductionsToTolerate(evt.target.value)
                  }
                >
                  {betType
                    .getNumberOfFaultingReductionsToTolerateOptions()
                    .map(option => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                </SelectInput>
                <span className="dropdown-after-label">
                  {getText(
                    'dialog-bet-config-tolerate-faulting-reductions-suffix',
                  )}
                </span>
              </label>
            </div>
          </div>
        )}

        <hr />

        {((ALLOW_SPARES && betType.hasSpareType()) ||
          hasV6 ||
          hasV7 ||
          betType.hasRaceNumber()) && (
          <>
            <div className="fgrid fgrid--dialog-form fgrid-dialog-form-wide-labels">
              {ALLOW_SPARES && betType.hasSpareType() && (
                <>
                  <div className="fgrid-cell fgrid-cell-label">
                    {getText('dialog-bet-config-spare-type')}
                    <HelpButton
                      header={getText('help-spare-type-header')}
                      body={getText('help-spare-type-body')}
                    />
                  </div>
                  <div className="fgrid-cell fgrid-cell-field">
                    {hasLoadedSettings && spareType ? (
                      <SelectInput
                        width="125"
                        value={spareType}
                        disabled={isLocked}
                        onChange={evt => setSpareType(evt.target.value)}
                      >
                        {betType.getSpareTypeOptions().map(option => (
                          <option key={option} value={option}>
                            {getText(
                              `dialog-bet-config-spare-type_${option.toLowerCase()}`,
                            )}
                          </option>
                        ))}
                      </SelectInput>
                    ) : (
                      <div className="dialog-spare-type-loader loader loader--visible">
                        <div className="loader__inner">
                          <div className="spinner" />
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}

              {hasV6 && (
                <>
                  <div className="fgrid-cell fgrid-cell-label">
                    {getText('dialog-bet-config-v6')}
                    <HelpButton
                      header={getText('help-v6-header')}
                      body={getText('help-v6-body')}
                    />
                  </div>
                  <div className="fgrid-cell fgrid-cell-field">
                    {hasLoadedSettings && typeof v6 === 'boolean' ? (
                      <CheckboxInput
                        checked={v6}
                        disabled={isLocked}
                        onChange={evt => setV6(evt.target.checked)}
                        colorFill
                      />
                    ) : (
                      <div className="dialog-v6-loader loader loader--visible">
                        <div className="loader__inner">
                          <div className="spinner" />
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}

              {hasV7 && (
                <>
                  <div className="fgrid-cell fgrid-cell-label">
                    {getText('dialog-bet-config-v7')}
                    <HelpButton
                      header={getText('help-v7-header')}
                      body={getText('help-v7-body')}
                    />
                  </div>
                  <div className="fgrid-cell fgrid-cell-field">
                    {hasLoadedSettings && typeof v7 === 'boolean' ? (
                      <CheckboxInput
                        checked={v7}
                        disabled={isLocked}
                        onChange={evt => setV7(evt.target.checked)}
                        colorFill
                      />
                    ) : (
                      <div className="dialog-v7-loader loader loader--visible">
                        <div className="loader__inner">
                          <div className="spinner" />
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}

              {betType.hasRaceNumber() && (
                <>
                  <div className="fgrid-cell fgrid-cell-label">
                    {getText('dialog-bet-config-race-number')}
                  </div>
                  <div className="fgrid-cell fgrid-cell-field">
                    <SelectInput
                      width="80"
                      value={raceNumber}
                      onChange={evt => setRaceNumber(evt.target.value)}
                      disabled={!isNewGame}
                    >
                      {betKeyInfo.getRaceNumbers().map(_raceNumber => (
                        <option key={_raceNumber} value={_raceNumber}>
                          {_raceNumber}
                        </option>
                      ))}
                    </SelectInput>
                  </div>
                </>
              )}
            </div>

            <hr />
          </>
        )}

        {betType.hasExtendedGuarantee() && (
          <>
            <div className="fgrid fgrid--dialog-form fgrid-dialog-form-wide-labels guarantee-percent">
              <div className="fgrid-cell fgrid-cell-label">
                {getText('dialog-bet-config-extended-guarantee-percent-label')}
                <HelpButton
                  header={getText('dialog-bet-config-guarantee-header')}
                  body={getText(
                    'dialog-bet-config-guarantee-help',
                    guaranteeOptions[betTypeName].slice(0, -1).join(','),
                    guaranteeOptions[betTypeName].slice(-1)[0],
                  )}
                />
              </div>
              <div className="fgrid-cell fgrid-cell-field has-extended-guarantee-percent">
                <div className="guarantee-wrapper">
                  <label className="radio-input">
                    {[...guaranteeOptions[betTypeName], 'percent'].map(
                      (option, index) => (
                        <RadioInput
                          key={option}
                          value={index}
                          checked={index === computedGuaranteeIndex}
                          disabled={
                            (option !== 'percent' &&
                              !numberOfErrorsGuaranteeAllowed) ||
                            isLocked
                          }
                          labelText={
                            option === 'percent'
                              ? getText(
                                  'dialog-bet-config-extended-guarantee-percent',
                                )
                              : getText(
                                  'dialog-bet-config-guarantee-option',
                                  option,
                                )
                          }
                          onChange={value => setGuaranteeIndex(value)}
                        />
                      ),
                    )}
                  </label>

                  <SelectInput
                    width="80"
                    className="guarantee-percent-dropdown"
                    value={guaranteePercent}
                    disabled={!isPercentGuaranteeIndex || isLocked}
                    onChange={evt => setGuaranteePercent(evt.target.value)}
                  >
                    {betType.getGuaranteePercentOptions().map(option => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </SelectInput>
                </div>
                <div className="explain">
                  {getText(
                    `dialog-bet-config-extended-guarantee-explain_${betTypeName.toLowerCase()}${
                      betTypeName === 'V75' && hasV7 ? '-v7' : ''
                    }`,
                  )}
                </div>
              </div>
            </div>

            <hr />
          </>
        )}

        {userIsTipCreator && (
          <>
            <PublishTimePicker
              userEmployer={userEmployer}
              isPublished={isPublished}
              setIsPublished={setIsPublished}
              publishDate={publishDate}
              nowMoment={nowMoment}
              disabled={isLocked}
              onPublishDateChange={onPublishDateChange}
            />
            <hr />
          </>
        )}

        {isNewGame && (
          <>
            <TemplatePicker
              betTypeName={betTypeName}
              betKeyInfo={betKeyInfo}
              selectedTemplate={template}
              setSelectedTemplate={setTemplate}
            />
            <hr />
          </>
        )}
      </form>

      <ul className="fgrid fgrid--1of2 button-list">
        <li className="fgrid-cell">
          <Button onClick={onConfirmConfig}>
            <span className="sprite dialog-confirm" />
            <span className="label positive">
              {getText('dialog-confirm-confirm')}
            </span>
          </Button>
        </li>
        <li className="fgrid-cell">
          <Button onClick={closeHandler}>
            <span className="sprite dialog-cancel" />
            <span className="label negative">
              {getText('dialog-confirm-cancel')}
            </span>
          </Button>
        </li>
      </ul>
    </>
  );
};

export default BetConfigDialog;
